// Træningsbane — lang, rolig blindskrift uden test (hybrid statisk + live API).

import { round } from '../data/curriculum/generators';
import { NIVEAU_5_UNLOCKED_KEYS } from '../data/curriculum/niveau-5';
import { accuracy, wpm } from './stats';
import { pickInitialFlowText } from './flowChunks';
import type { FlowLexicon, PracticeMode, RoundResult, Trin, UserProfile } from '../types';

export const FLOW_PRACTICE_TRIN_ID = '__flow__';

/** Ønsket længde per tekst (ét emne ad gangen). */
export const FLOW_PASSAGE_MIN_CHARS = 220;
export const FLOW_PASSAGE_MAX_CHARS = 320;

/** Kun efter hele alfabetet inkl. store bogstaver (niveau 5). */
export function isFlowPracticeEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-5');
}

export function effectiveFlowLexicon(user: UserProfile): FlowLexicon {
  return user.preferences?.flowLexicon === 'en' ? 'en' : 'da';
}

export function flowLexiconLabel(lexicon: FlowLexicon): string {
  return lexicon === 'en' ? 'Engelsk' : 'Dansk';
}

export function flowTopicLabel(topic: string): string {
  if (topic.startsWith('sætning-')) return 'Tekst';
  return topic.charAt(0).toUpperCase() + topic.slice(1);
}

/** Shell trin — sprog vælges i setup før lektionen bygges. */
export function buildFlowPracticeShellTrin(): Trin {
  return {
    id: FLOW_PRACTICE_TRIN_ID,
    tierId: 'niveau-5',
    trinNumber: 0,
    title: 'Træningsbane',
    description: 'Skriv så længe du har lyst. Stop når du er færdig.',
    goal: 'Rolig blindskrift — uden test og uden tidspres.',
    unlockedKeys: [...NIVEAU_5_UNLOCKED_KEYS],
    newKeys: [],
    strictCase: true,
    rounds: [],
  };
}

export function buildFlowPracticeTrin(
  user: UserProfile,
  lexicon: FlowLexicon = effectiveFlowLexicon(user),
): Trin | null {
  if (!isFlowPracticeEligible(user)) return null;

  const id = FLOW_PRACTICE_TRIN_ID;
  const langLabel = flowLexiconLabel(lexicon);

  return {
    id,
    tierId: 'niveau-5',
    trinNumber: 0,
    title: 'Træningsbane',
    description: `Skriv så længe du har lyst — ${langLabel.toLowerCase()} tekst med hele alfabetet.`,
    goal: 'Rolig blindskrift — uden test og uden tidspres.',
    unlockedKeys: [...NIVEAU_5_UNLOCKED_KEYS],
    newKeys: [],
    strictCase: true,
    rounds: [
      round({
        id: `${id}-r1`,
        title: 'Skriv videre',
        purpose:
          'Ét emne ad gangen — ca. 250–300 tegn. Næste emne med Space når du er klar.',
        mode: 'recall',
        generate: (opts) =>
          pickInitialFlowText(
            opts.flowLexicon === 'en' ? 'en' : lexicon,
            opts.seed,
          ),
        presentation: { kind: 'flow' },
      }),
    ],
  };
}

/** Saml segmenter fra flere emner til ét round-resultat. */
export function mergeFlowSegmentResults(
  segments: RoundResult[],
  meta: {
    roundId: string;
    title: string;
    mode: PracticeMode;
    flowLexicon: FlowLexicon;
  },
): RoundResult {
  const durationMs = segments.reduce((a, r) => a + r.durationMs, 0);
  const correctChars = segments.reduce((a, r) => a + r.correctChars, 0);
  const totalKeystrokes = segments.reduce((a, r) => a + r.totalKeystrokes, 0);
  const mistakes = segments.reduce((a, r) => a + r.mistakes, 0);
  const backspaces = segments.reduce((a, r) => a + r.backspaces, 0);
  const targetLength = segments.reduce((a, r) => a + r.targetLength, 0);

  const errorsByKey: Record<string, number> = {};
  const responseTimes: Record<string, number[]> = {};
  for (const r of segments) {
    for (const [k, v] of Object.entries(r.errorsByKey)) {
      errorsByKey[k] = (errorsByKey[k] ?? 0) + v;
    }
    for (const [k, v] of Object.entries(r.responseTimes)) {
      if (!responseTimes[k]) responseTimes[k] = [];
      responseTimes[k].push(...v);
    }
  }

  return {
    roundId: meta.roundId,
    title: meta.title,
    mode: meta.mode,
    targetLength,
    durationMs,
    wpm: wpm(correctChars, durationMs),
    accuracy: accuracy(correctChars, totalKeystrokes),
    correctChars,
    totalKeystrokes,
    mistakes,
    backspaces,
    errorsByKey,
    responseTimes,
    completed: segments.every((r) => r.completed),
    hintsUsed: segments.some((r) => r.hintsUsed),
    presentationKind: 'flow',
    flowLapCount: segments.length,
    flowLexicon: meta.flowLexicon,
  };
}
